import { Routes } from '@angular/router'
import { isAuthenticated } from '../../core/auth/guards/auth.guard'
import { adDetailsRedirectGuard } from '../ads/ad-details/ad-details-redirect.guard'

export const publicRoutes: Routes = [
	{
		path: '',
		loadComponent: () => import('./landing-page/landing-page'),
		data: {
			preload: true,
		},
	},
	{
		path: 'login',
		loadComponent: () => import('./login/login'),
	},
	{
		path: 'dashboard',
		loadComponent: () => import('./dashboard-page/dashboard-page'),
		canActivate: [isAuthenticated],
		data: {
			preload: true,
		},
	},
	{
		path: 'sign-up',
		loadComponent: () => import('./login/login'),
	},
	{
		path: 'authorization',
		loadComponent: () => import('./authorization/authorization'),
		canActivate: [isAuthenticated],
	},
	{
		path: 'configurator',
		loadComponent: () => import('./configurator/configurator'),
	},
	{
		path: 'request-role',
		loadComponent: () => import('./request-role/request-role'),
		canActivate: [isAuthenticated],
	},
	{
		path: 'courses',
		loadComponent: () => import('./courses-page/courses-page'),
	},
	{
		// Academy M4: side-by-side course comparison (guest-accessible, PRD ch.24).
		path: 'courses-compare',
		loadComponent: () => import('./courses-compare/courses-compare'),
		data: { title: 'مقایسه دوره‌ها', breadcrumb: 'مقایسه' },
	},
	/**
	 * Academy M2: canonical course detail — /courses/{id}-{slug}. The component
	 * extracts the ad UUID from the composite segment. SSR-rendered (SEO).
	 */
	{
		path: 'courses/:idSlug',
		loadComponent: () => import('./course-details/course-details'),
		data: {
			title: 'جزئیات دوره',
			breadcrumb: 'دوره',
		},
	},
	/**
	 * Public instructor profile (academy). Slug or UUID in the path; SSR-rendered
	 * for SEO (see app.routes.server.ts).
	 */
	{
		path: 'instructors/:slugOrId',
		loadComponent: () => import('./instructor-profile-page/instructor-profile-page'),
		data: {
			title: 'پروفایل مدرس',
			breadcrumb: 'مدرس',
		},
	},
	/**
	 * Marketplace M7/M8: public seller profile — badges, trust score and the
	 * seller's reviews. Keycloak UUID in the path; SSR-rendered for SEO
	 * (see app.routes.server.ts).
	 */
	{
		path: 'sellers/:kcUserId',
		loadComponent: () => import('./seller-profile-page/seller-profile-page'),
		data: {
			title: 'پروفایل فروشنده',
			breadcrumb: 'فروشنده',
		},
	},
	{
		// Unified ad search: type switcher + per-type filters (space/course/equipment).
		path: 'search',
		loadComponent: () => import('./search-page/search-page'),
	},
	{
		path: 'equipment',
		loadComponent: () => import('./equipment-page/equipment-page'),
	},
	{
		// Marketplace M3: side-by-side equipment comparison (guest, localStorage-backed).
		path: 'equipment-compare',
		loadComponent: () => import('./equipment-compare/equipment-compare'),
		data: { title: 'مقایسه تجهیزات', breadcrumb: 'مقایسه' },
	},
	{
		// Marketplace M5: gateway return page for secure (escrow) transactions.
		// NOTE: keep BEFORE the parameterized `equipment/checkout/:adId` route.
		path: 'equipment/checkout/callback',
		loadComponent: () => import('./secure-payment-callback/secure-payment-callback'),
		data: { title: 'تأیید پرداخت امن', breadcrumb: 'پرداخت' },
	},
	{
		// Marketplace M5: secure (escrow) checkout for an equipment ad.
		path: 'equipment/checkout/:adId',
		loadComponent: () => import('./equipment-checkout/equipment-checkout'),
		canActivate: [isAuthenticated],
		data: { title: 'خرید با پرداخت امن', breadcrumb: 'چک‌اوت' },
	},
	/**
	 * Marketplace M3: canonical equipment detail — /equipment/{id}-{slug}. The
	 * component extracts the ad UUID from the composite segment. SSR-rendered (SEO).
	 */
	{
		path: 'equipment/:idSlug',
		loadComponent: () => import('./equipment-details/equipment-details'),
		data: {
			title: 'جزئیات تجهیزات',
			breadcrumb: 'تجهیزات',
		},
	},
	{
		path: 'auctions',
		loadComponent: () => import('./auctions-page/auctions-page'),
	},
	{
		path: 'partner',
		loadComponent: () => import('./partner-page/partner-page'),
	},
	{
		path: 'brand-advertising',
		loadComponent: () => import('./brand-advertising-page/brand-advertising-page'),
	},
	{
		path: 'pro-ads',
		loadComponent: () => import('./landing-page/pro-ads/pro-ads'),
	},
	{
		path: 'guide',
		loadComponent: () => import('./guide-page/guide-page'),
	},
	{
		path: 'ads-list',
		loadComponent: () => import('./space-rental-list/space-rental-list'),
	},
	/**
	 * Canonical, SEO-friendly ad detail route. Path segment holds the ad id so the
	 * URL reads `https://beautyboss.ir/ads/space-rental/{id}` (matching the sitemap).
	 * Component input binding (`withComponentInputBinding`) maps the `:id` param to
	 * the component's `id` input signal.
	 */
	{
		path: 'ads/space-rental/:id',
		loadComponent: () => import('../ads/ad-details/ad-details'),
		data: {
			title: 'جزئیات آگهی',
			breadcrumb: 'جزئیات',
		},
	},
	/**
	 * Legacy deep-link compatibility: `/ad-details?id=<id>` 301-style redirects to
	 * the canonical path route above. Keep until external links/bookmarks migrate.
	 */
	{
		path: 'ad-details',
		canActivate: [adDetailsRedirectGuard],
		loadComponent: () => import('../ads/ad-details/ad-details'),
	},
	/**
	 * Legacy segment-style deep links (`/ad-details/<id>`) — this is the URL
	 * shape the backend-generated sitemap emits and old indexed pages use.
	 * Redirect them to the canonical route so they keep resolving (and pass
	 * their link equity) instead of falling through to the catch-all 404.
	 */
	{
		path: 'ad-details/:id',
		redirectTo: route => `/ads/space-rental/${route.paramMap.get('id')}`,
	},
	{
		path: 'ads-comparison',
		loadComponent: () => import('../public/ads-comparison/ads-comparison'),
		data: {
			title: 'Admin Dashboard',
			breadcrumb: 'Dashboard',
		},
	},
	{
		path: 'payment-callback',
		loadComponent: () => import('../ads/payment-callback/payment-callback'),
		data: {
			title: 'تایید پرداخت',
			breadcrumb: 'پرداخت',
		},
	},
]
